import { Routes } from 'app/config/routes/consts';
import { Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { useMutation } from 'react-query';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { enqueueSnackbar } from 'notistack';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { twoFactorRecovery, twoFactorVerify } from 'entities/Auth/api';
import { AuthResponse, EAuthStep } from 'entities/Auth/api/types';
import { useAuthStore } from 'processes/auth/model/state';
import { ColorPalette } from 'shared/consts/colorPalette';
import { isEmptyObject } from 'shared/helpers/isEmptyObject';
import { isEmptyObjectValues } from 'shared/helpers/isEmptyObjectValues';
import { BackButton } from 'shared/ui/BackButton';
import { Button } from 'shared/ui/Button';
import { InputField } from 'shared/ui/InputField';
import { Paper } from 'shared/ui/Paper';
import {
	TwoFactorCode,
	TwoFactorCodeValidationSchema,
	TwoFactorRecovery,
	TwoFactorRecoveryValidationSchema,
} from 'pages/auth/TwoFactor/twoFactorValidation';
import { getErrorMessage } from 'shared/helpers/getErrorMessage';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { useTwoFactorStore } from 'pages/auth/TwoFactor/twoFactorStore';

const LOW_BACKUP_CODES_THRESHOLD = 2;

export const TwoFactorVerify: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { updateAuthData } = useAuthStore();
	const { challenge, reset } = useTwoFactorStore();
	const [isRecoveryMode, setIsRecoveryMode] = useState(false);

	const completeSignIn = (data: AuthResponse) => {
		reset();
		updateAuthData(data);
	};

	const { mutate: verify, isLoading: isVerifyLoading } = useMutation({
		mutationKey: ['post/2fa-verify'],
		mutationFn: twoFactorVerify,
		onSuccess: (res) => completeSignIn(res.data),
	});

	const { mutate: recover, isLoading: isRecoveryLoading } = useMutation({
		mutationKey: ['post/2fa-recovery'],
		mutationFn: twoFactorRecovery,
		onSuccess: (res) => {
			const { remainingBackupCodesCount, ...authData } = res.data;
			if (
				remainingBackupCodesCount !== undefined &&
				remainingBackupCodesCount <= LOW_BACKUP_CODES_THRESHOLD
			) {
				enqueueSnackbar({
					message: t('auth.two_factor.low_backup_codes', { count: remainingBackupCodesCount }),
					variant: 'warning',
					persist: true,
				});
			}
			completeSignIn(authData);
		},
	});

	if (!challenge || challenge.step !== EAuthStep.TWO_FACTOR_VERIFY) {
		return <Navigate to={Routes.signIn} replace />;
	}

	const goToSignIn = () => {
		reset();
		navigate(Routes.signIn, { replace: true });
	};

	const onError = (field: string, setFieldError: (field: string, message?: string) => void) => {
		return (err: unknown) => {
			if ((err as AxiosError).response?.status === 401) {
				showErrorNotification(err as AxiosError);
				goToSignIn();
				return;
			}
			setFieldError(field, getErrorMessage(err));
		};
	};

	const handleVerify = (values: TwoFactorCode, { setFieldError }: FormikHelpers<TwoFactorCode>) => {
		verify(
			{ twoFactorToken: challenge.twoFactorToken, code: values.code.trim() },
			{ onError: onError('code', setFieldError) },
		);
	};

	const handleRecovery = (
		values: TwoFactorRecovery,
		{ setFieldError }: FormikHelpers<TwoFactorRecovery>,
	) => {
		recover(
			{ twoFactorToken: challenge.twoFactorToken, recoveryCode: values.recoveryCode.trim() },
			{ onError: onError('recoveryCode', setFieldError) },
		);
	};

	const modeSwitcher = (
		<Typography
			variant={'body2'}
			color={ColorPalette.royalBlue}
			onClick={() => setIsRecoveryMode((prev) => !prev)}
			sx={{ cursor: 'pointer', marginTop: '24px', textAlign: 'center' }}
		>
			{isRecoveryMode ? t('auth.two_factor.use_authenticator') : t('auth.two_factor.use_recovery_code')}
		</Typography>
	);

	return (
		<Paper
			title={
				(isRecoveryMode
					? t('auth.two_factor.recovery_title')
					: t('auth.two_factor.verify_title')) as string
			}
		>
			<BackButton action={goToSignIn} />
			<Typography variant={'body1'} color={ColorPalette.baliHai} sx={{ marginBottom: '35px' }}>
				{isRecoveryMode
					? t('auth.two_factor.recovery_description')
					: t('auth.two_factor.verify_description')}
			</Typography>

			{isRecoveryMode ? (
				<Formik
					key={'recovery'}
					initialValues={{ recoveryCode: '' }}
					validationSchema={TwoFactorRecoveryValidationSchema}
					onSubmit={handleRecovery}
				>
					{({ errors, values }: FormikProps<TwoFactorRecovery>) => (
						<Form>
							<InputField
								name={'recoveryCode'}
								label={t('auth.two_factor.recovery_code_label')}
								placeholder={t('auth.two_factor.recovery_code_placeholder')}
								autoComplete={'off'}
								inputProps={{ maxLength: 9 }}
								autoFocus
								required
							/>
							<Button
								disabled={
									isRecoveryLoading || !isEmptyObject(errors) || isEmptyObjectValues(values)
								}
								type={'submit'}
								fullWidth
								sx={{ marginTop: '12px' }}
							>
								{t('auth.sign_in')}
							</Button>
						</Form>
					)}
				</Formik>
			) : (
				<Formik
					key={'totp'}
					initialValues={{ code: '' }}
					validationSchema={TwoFactorCodeValidationSchema}
					onSubmit={handleVerify}
				>
					{({ errors, values }: FormikProps<TwoFactorCode>) => (
						<Form>
							<InputField
								name={'code'}
								label={t('auth.two_factor.code_label')}
								placeholder={t('auth.two_factor.code_placeholder')}
								autoComplete={'one-time-code'}
								inputProps={{ inputMode: 'numeric', maxLength: 6 }}
								autoFocus
								required
							/>
							<Button
								disabled={
									isVerifyLoading || !isEmptyObject(errors) || isEmptyObjectValues(values)
								}
								type={'submit'}
								fullWidth
								sx={{ marginTop: '12px' }}
							>
								{t('auth.sign_in')}
							</Button>
						</Form>
					)}
				</Formik>
			)}

			{modeSwitcher}
		</Paper>
	);
};
